"use client";

import { forwardRef, useImperativeHandle, useRef } from "react";

import {
  titrationTapSettingLabel,
  type TitrationGuidanceTarget,
} from "./TitrationGuidedController";
import styles from "./titration-spatial-overlay.module.css";

export type TitrationOverlayAnchorId =
  | "meniscus"
  | "tip"
  | "tap"
  | "flaskNeck"
  | "flaskBody"
  | "record";

export type OverlayPoint = {
  x: number;
  y: number;
  visible: boolean;
};

export type OverlayRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

export type TitrationOverlayProjection = {
  transitioning: boolean;
  viewport: {
    width: number;
    height: number;
    touchCapable: boolean;
  };
  anchors: Record<TitrationOverlayAnchorId, OverlayPoint>;
  apparatusBounds: OverlayRect | null;
};

export type OverlayLeaderDebug = {
  anchor: [number, number];
  end: [number, number];
  length: number;
};

export type TitrationOverlayDebugState = {
  anchors: Record<TitrationOverlayAnchorId, [number, number] | null>;
  apparatusBounds: OverlayRect | null;
  overlays: {
    reading: OverlayRect | null;
    targetLabel: OverlayRect | null;
    stepAway: OverlayRect | null;
  };
  target: TitrationGuidanceTarget;
  leaderLines: {
    reading: OverlayLeaderDebug | null;
    target: OverlayLeaderDebug | null;
  };
};

export type TitrationSpatialOverlayHandle = {
  updateLayout: (projection: TitrationOverlayProjection) => void;
  getDebugState: () => TitrationOverlayDebugState;
};

type TitrationSpatialOverlayProps = {
  readingMl: number;
  tapOpenFraction: number;
  target: TitrationGuidanceTarget;
  targetLabel: string | null;
  onStepAway: () => void;
};

type OverlayKind = "reading" | "target";
type Size = { width: number; height: number };
type Candidate = { left: number; top: number; preference: number };
type LeaderRefs = {
  line: SVGLineElement | null;
  dot: SVGCircleElement | null;
};

const FALLBACK_SIZES: Record<OverlayKind, Size> = {
  reading: { width: 124, height: 58 },
  target: { width: 118, height: 42 },
};

function createRect(left: number, top: number, width: number, height: number) {
  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    height,
  };
}

function cloneRect(rect: OverlayRect | null) {
  return rect ? { ...rect } : null;
}

function expandRect(rect: OverlayRect, padding: number) {
  return createRect(
    rect.left - padding,
    rect.top - padding,
    rect.width + padding * 2,
    rect.height + padding * 2
  );
}

export function intersectionArea(first: OverlayRect, second: OverlayRect) {
  const width = Math.max(
    0,
    Math.min(first.right, second.right) - Math.max(first.left, second.left)
  );
  const height = Math.max(
    0,
    Math.min(first.bottom, second.bottom) - Math.max(first.top, second.top)
  );
  return width * height;
}

function outsideSafePixels(rect: OverlayRect, safe: OverlayRect) {
  return (
    Math.max(0, safe.left - rect.left) +
    Math.max(0, rect.right - safe.right) +
    Math.max(0, safe.top - rect.top) +
    Math.max(0, rect.bottom - safe.bottom)
  );
}

function closestPointOnRect(anchor: OverlayPoint, rect: OverlayRect) {
  return {
    x: Math.min(Math.max(anchor.x, rect.left), rect.right),
    y: Math.min(Math.max(anchor.y, rect.top), rect.bottom),
  };
}

function leaderLength(anchor: OverlayPoint, rect: OverlayRect) {
  const end = closestPointOnRect(anchor, rect);
  return Math.hypot(end.x - anchor.x, end.y - anchor.y);
}

function candidatesFor(
  kind: OverlayKind,
  anchor: OverlayPoint,
  size: Size
): Candidate[] {
  const { width, height } = size;
  const horizontal = kind === "reading" ? 18 : 16;
  const vertical = kind === "reading" ? 18 : 14;
  return [
    {
      left: anchor.x - width - horizontal,
      top: anchor.y - height / 2,
      preference: 0,
    },
    {
      left: anchor.x + horizontal,
      top: anchor.y - height / 2,
      preference: 1,
    },
    {
      left: anchor.x - width / 2,
      top: anchor.y - height - vertical,
      preference: 2,
    },
    {
      left: anchor.x - width / 2,
      top: anchor.y + vertical,
      preference: 3,
    },
  ];
}

// Test the actual leader segment, including an anchor hidden behind a control.
function leaderCrosses(
  anchor: OverlayPoint,
  badge: OverlayRect,
  obstacle: OverlayRect
) {
  const end = closestPointOnRect(anchor, badge);
  let start = 0;
  let finish = 1;
  for (const [origin, delta, low, high] of [
    [anchor.x, end.x - anchor.x, obstacle.left, obstacle.right],
    [anchor.y, end.y - anchor.y, obstacle.top, obstacle.bottom],
  ]) {
    if (delta === 0) {
      if (origin < low || origin > high) return false;
    } else {
      const first = (low - origin) / delta;
      const last = (high - origin) / delta;
      start = Math.max(start, Math.min(first, last));
      finish = Math.min(finish, Math.max(first, last));
      if (start > finish) return false;
    }
  }
  return true;
}

export function chooseOverlayPlacement({
  kind,
  anchor,
  size,
  safeRect,
  apparatusRect,
  occupied,
}: {
  kind: OverlayKind;
  anchor: OverlayPoint;
  size: Size;
  safeRect: OverlayRect;
  apparatusRect: OverlayRect | null;
  occupied: OverlayRect[];
}) {
  const candidates = candidatesFor(kind, anchor, size);
  if (apparatusRect) {
    const gap = safeRect.width <= 760 ? 4 : 12;
    candidates.push(
      {
        left: apparatusRect.left - size.width - gap,
        top: anchor.y - size.height / 2,
        preference: 4,
      },
      {
        left: apparatusRect.right + gap,
        top: anchor.y - size.height / 2,
        preference: 5,
      },
      {
        left: anchor.x - size.width / 2,
        top: apparatusRect.bottom + gap,
        preference: 6,
      }
    );
  }

  function pick(options: Candidate[]) {
    return (
      options
        .map((candidate) => {
          const rect = createRect(
            candidate.left,
            candidate.top,
            size.width,
            size.height
          );
          return {
            rect,
            score:
              (apparatusRect
                ? intersectionArea(rect, apparatusRect) * 3_000
                : 0) +
              leaderLength(anchor, rect) +
              candidate.preference * 120,
          };
        })
        .filter(
          ({ rect }) =>
            outsideSafePixels(rect, safeRect) === 0 &&
            occupied.every(
              (other) =>
                intersectionArea(rect, other) === 0 &&
                (kind === "reading" || !leaderCrosses(anchor, rect, other))
            )
        )
        .sort((a, b) => a.score - b.score)[0]?.rect ?? null
    );
  }

  const preferred = pick(candidates);
  if (preferred || kind === "target") return preferred;

  // Reading has priority. Try only viewport/control edges (at most 36 pairs
  // for the guide and Step away), never an open-ended pixel search.
  const xs = [safeRect.left, safeRect.right - size.width];
  const ys = [safeRect.top, safeRect.bottom - size.height];
  for (const rect of occupied) {
    xs.push(rect.left - size.width - 4, rect.right + 4);
    ys.push(rect.top - size.height - 4, rect.bottom + 4);
  }
  return pick(
    xs.flatMap((left) => ys.map((top) => ({ left, top, preference: 0 })))
  );
}

function sizeForElement(element: HTMLElement, fallback: Size): Size {
  const rect = element.getBoundingClientRect();
  return {
    width: element.offsetWidth || rect.width || fallback.width,
    height: element.offsetHeight || rect.height || fallback.height,
  };
}

function rectFromElement(element: HTMLElement | null) {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  return createRect(rect.left, rect.top, rect.width, rect.height);
}

function setElementPlacement(
  element: HTMLElement | null,
  rect: OverlayRect | null,
  visible: boolean
) {
  if (!element) return;
  if (!visible || !rect) {
    element.style.opacity = "0";
    element.style.visibility = "hidden";
    return;
  }
  element.style.transitionProperty = "opacity";
  element.style.transform = `translate3d(${rect.left.toFixed(
    2
  )}px, ${rect.top.toFixed(2)}px, 0)`;
  element.style.opacity = "1";
  element.style.visibility = "visible";
}

function updateLeader(
  refs: LeaderRefs,
  anchor: OverlayPoint,
  rect: OverlayRect | null,
  visible: boolean
): OverlayLeaderDebug | null {
  const { line, dot } = refs;
  if (!line || !dot || !visible || !rect) {
    line?.setAttribute("visibility", "hidden");
    dot?.setAttribute("visibility", "hidden");
    return null;
  }
  const end = closestPointOnRect(anchor, rect);
  line.setAttribute("x1", anchor.x.toFixed(2));
  line.setAttribute("y1", anchor.y.toFixed(2));
  line.setAttribute("x2", end.x.toFixed(2));
  line.setAttribute("y2", end.y.toFixed(2));
  line.setAttribute("visibility", "visible");
  dot.setAttribute("cx", anchor.x.toFixed(2));
  dot.setAttribute("cy", anchor.y.toFixed(2));
  dot.setAttribute("visibility", "visible");
  return {
    anchor: [anchor.x, anchor.y],
    end: [end.x, end.y],
    length: Number(Math.hypot(end.x - anchor.x, end.y - anchor.y).toFixed(2)),
  };
}

function targetAnchor(
  target: TitrationGuidanceTarget,
  anchors: Record<TitrationOverlayAnchorId, OverlayPoint>
) {
  switch (target) {
    case "tip":
      return anchors.tip;
    case "tap":
      return anchors.tap;
    case "flask":
      return anchors.flaskBody;
    case "reading":
      return anchors.meniscus;
    case null:
      return null;
  }
}

const TitrationSpatialOverlay = forwardRef<
  TitrationSpatialOverlayHandle,
  TitrationSpatialOverlayProps
>(function TitrationSpatialOverlay(
  { readingMl, tapOpenFraction, target, targetLabel, onStepAway },
  ref
) {
  const overlayRef = useRef<HTMLElement>(null);
  const readingBadgeRef = useRef<HTMLDivElement>(null);
  const targetLabelRef = useRef<HTMLDivElement>(null);
  const stepAwayRef = useRef<HTMLButtonElement>(null);
  const leaderSvgRef = useRef<SVGSVGElement>(null);
  const readingLineRef = useRef<SVGLineElement>(null);
  const readingDotRef = useRef<SVGCircleElement>(null);
  const targetLineRef = useRef<SVGLineElement>(null);
  const targetDotRef = useRef<SVGCircleElement>(null);
  const debugRef = useRef<TitrationOverlayDebugState>({
    anchors: {
      meniscus: null,
      tip: null,
      tap: null,
      flaskNeck: null,
      flaskBody: null,
      record: null,
    },
    apparatusBounds: null,
    overlays: { reading: null, targetLabel: null, stepAway: null },
    target: null,
    leaderLines: { reading: null, target: null },
  });

  useImperativeHandle(
    ref,
    () => ({
      updateLayout(projection) {
        const svg = leaderSvgRef.current;
        if (svg) {
          svg.setAttribute(
            "viewBox",
            `0 0 ${projection.viewport.width} ${projection.viewport.height}`
          );
        }
        const landscapeMobile =
          projection.viewport.touchCapable &&
          projection.viewport.height <= 500 &&
          projection.viewport.width > projection.viewport.height;
        const horizontalSafe = projection.viewport.touchCapable ? 8 : 12;
        const safeRect = createRect(
          horizontalSafe,
          landscapeMobile ? 10 : 84,
          projection.viewport.width - horizontalSafe * 2,
          projection.viewport.height - (landscapeMobile ? 22 : 96)
        );
        const apparatusRect = projection.apparatusBounds
          ? expandRect(
              projection.apparatusBounds,
              projection.viewport.width <= 760 ? 2 : 10
            )
          : null;
        const occupied: OverlayRect[] = [];
        const origin = overlayRef.current?.getBoundingClientRect();
        const localRect = (element: HTMLElement | null) => {
          const rect = rectFromElement(element);
          return rect
            ? createRect(
                rect.left - (origin?.left ?? 0),
                rect.top - (origin?.top ?? 0),
                rect.width,
                rect.height
              )
            : null;
        };
        // Read each frame: card wrapping, scrolling and responsive layout can change.
        const guide =
          overlayRef.current?.parentElement?.querySelector<HTMLElement>(
            "[data-guided-step]"
          ) ?? null;
        const guideRect = localRect(guide);
        const stepAwayRect = localRect(stepAwayRef.current);
        if (guideRect) occupied.push(guideRect);
        if (stepAwayRect) occupied.push(stepAwayRect);
        const localVisible = !projection.transitioning;

        const readingAnchor = projection.anchors.meniscus;
        const readingRect =
          localVisible && readingAnchor.visible && readingBadgeRef.current
            ? chooseOverlayPlacement({
                kind: "reading",
                anchor: readingAnchor,
                size: sizeForElement(
                  readingBadgeRef.current,
                  FALLBACK_SIZES.reading
                ),
                safeRect,
                apparatusRect,
                occupied,
              })
            : null;
        const readingLeaderClear =
          readingRect &&
          occupied.every(
            (rect) => !leaderCrosses(readingAnchor, readingRect, rect)
          );
        if (readingRect) occupied.push(readingRect);
        setElementPlacement(
          readingBadgeRef.current,
          readingRect,
          Boolean(readingRect)
        );

        const activeAnchor = targetAnchor(target, projection.anchors);
        const showTarget =
          target !== null && target !== "reading" && Boolean(targetLabel);
        const targetRect =
          localVisible &&
          showTarget &&
          activeAnchor?.visible &&
          targetLabelRef.current
            ? chooseOverlayPlacement({
                kind: "target",
                anchor: activeAnchor,
                size: sizeForElement(
                  targetLabelRef.current,
                  FALLBACK_SIZES.target
                ),
                safeRect,
                apparatusRect,
                occupied,
              })
            : null;
        setElementPlacement(
          targetLabelRef.current,
          targetRect,
          Boolean(targetRect)
        );

        const readingLeader = updateLeader(
          { line: readingLineRef.current, dot: readingDotRef.current },
          readingAnchor,
          readingRect,
          Boolean(readingRect && readingLeaderClear)
        );
        const targetLeader = activeAnchor
          ? updateLeader(
              { line: targetLineRef.current, dot: targetDotRef.current },
              activeAnchor,
              targetRect,
              Boolean(targetRect)
            )
          : null;

        debugRef.current = {
          anchors: Object.fromEntries(
            Object.entries(projection.anchors).map(([id, point]) => [
              id,
              point.visible ? [point.x, point.y] : null,
            ])
          ) as TitrationOverlayDebugState["anchors"],
          apparatusBounds: cloneRect(apparatusRect),
          overlays: {
            reading: cloneRect(readingRect),
            targetLabel: cloneRect(targetRect),
            stepAway: cloneRect(stepAwayRect),
          },
          target,
          leaderLines: { reading: readingLeader, target: targetLeader },
        };
      },
      getDebugState() {
        return JSON.parse(
          JSON.stringify(debugRef.current)
        ) as TitrationOverlayDebugState;
      },
    }),
    [target, targetLabel]
  );

  return (
    <section
      ref={overlayRef}
      className={styles.overlayLayer}
      aria-label="3D acid-base titration controls"
    >
      <svg ref={leaderSvgRef} className={styles.leaderLayer} aria-hidden="true">
        <line ref={readingLineRef} className={styles.leaderLine} />
        <circle ref={readingDotRef} className={styles.leaderDot} />
        <line ref={targetLineRef} className={styles.targetLine} />
        <circle ref={targetDotRef} className={styles.targetDot} />
      </svg>

      <div
        ref={readingBadgeRef}
        className={`${styles.positionedOverlay} ${styles.readingBadge} ${
          target === "reading" ? styles.readingBadgeActive : ""
        }`}
        data-reading-badge
        role="group"
        aria-label="Burette reading"
      >
        <strong>{readingMl.toFixed(2)} mL</strong>
        <span>
          {tapOpenFraction > 0
            ? `Tap open · ${titrationTapSettingLabel(tapOpenFraction)}`
            : "Tap closed"}
        </span>
      </div>

      {targetLabel ? (
        <div
          ref={targetLabelRef}
          className={`${styles.positionedOverlay} ${styles.targetLabel}`}
          data-guidance-target={target ?? undefined}
          aria-hidden="true"
        >
          {targetLabel}
        </div>
      ) : null}

      <button
        ref={stepAwayRef}
        type="button"
        className={styles.stepAway}
        aria-label="Return to laboratory walk"
        onClick={onStepAway}
      >
        Step away
      </button>
    </section>
  );
});

export default TitrationSpatialOverlay;
